import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { useIdleGuard, IdleWarningDialog } from "@/components/auth/IdleGuard";
import { connectivity } from "@/services/connectivity";

const AuthContext = createContext(null);
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Derive color variants from a hex color */
function hexToHSL(hex) {
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}

function hslToHex(h, s, l) {
  s /= 100; l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = n => { const k = (n + h / 30) % 12; return l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1); };
  return '#' + [f(0), f(8), f(4)].map(x => Math.round(x * 255).toString(16).padStart(2, '0')).join('');
}

function lighten(hex, amount) {
  const [h, s, l] = hexToHSL(hex);
  return hslToHex(h, s, Math.min(100, l + amount));
}

function darken(hex, amount) {
  const [h, s, l] = hexToHSL(hex);
  return hslToHex(h, s, Math.max(0, l - amount));
}

function withAlpha(hex, alpha) {
  return hex + Math.round(alpha * 255).toString(16).padStart(2, '0');
}

export const BRAND_FONTS = {
  default: { label: "Default (Theme)", css: "", url: "" },
  cairo: { label: "Cairo (عربي + Latin)", css: "'Cairo', sans-serif", url: "Cairo:wght@400;500;600;700" },
  tajawal: { label: "Tajawal (عربي + Latin)", css: "'Tajawal', sans-serif", url: "Tajawal:wght@400;500;700" },
  almarai: { label: "Almarai (عربي)", css: "'Almarai', sans-serif", url: "Almarai:wght@400;700" },
  ibm_plex_arabic: { label: "IBM Plex Sans Arabic", css: "'IBM Plex Sans Arabic', sans-serif", url: "IBM+Plex+Sans+Arabic:wght@400;500;600;700" },
  poppins: { label: "Poppins", css: "'Poppins', sans-serif", url: "Poppins:wght@400;500;600;700" },
  inter: { label: "Inter", css: "'Inter', sans-serif", url: "Inter:wght@400;500;600;700" },
  nunito: { label: "Nunito", css: "'Nunito', sans-serif", url: "Nunito:wght@400;600;700" },
  roboto: { label: "Roboto", css: "'Roboto', sans-serif", url: "Roboto:wght@400;500;700" },
  playfair: { label: "Playfair Display (Serif)", css: "'Playfair Display', serif", url: "Playfair+Display:wght@400;500;600;700" },
};

export const BRAND_FONT_SIZES = {
  small: { label: "Small", px: "14px" },
  normal: { label: "Normal", px: "" },
  large: { label: "Large", px: "17.5px" },
  xlarge: { label: "Extra Large", px: "19px" },
};

const applyBrandFont = (key) => {
  const root = document.documentElement;
  const f = BRAND_FONTS[key];
  if (!key || key === "default" || !f || !f.css) {
    document.body.style.removeProperty("font-family");
    root.style.removeProperty("--brand-font");
    return;
  }
  const id = `brand-font-${key}`;
  if (f.url && !document.getElementById(id)) {
    const l = document.createElement("link");
    l.id = id;
    l.rel = "stylesheet";
    l.href = `https://fonts.googleapis.com/css2?family=${f.url}&display=swap`;
    document.head.appendChild(l);
  }
  document.body.style.fontFamily = f.css;
  root.style.setProperty("--brand-font", f.css);
};

const applyBrandFontSize = (key) => {
  const v = BRAND_FONT_SIZES[key]?.px;
  if (v) document.documentElement.style.fontSize = v;
  else document.documentElement.style.removeProperty("font-size");
};

const applyBrandingToDOM = (branding) => {
  if (!branding) return;
  const root = document.documentElement;
  BRAND_VARS.forEach(v => root.style.removeProperty(v));
  const pc = branding.primary_color;
  const ac = branding.accent_color;

  if (pc) {
    const isDark = root.classList.contains('dark-mode') || document.body.classList.contains('dark-mode');
    const pcLight = lighten(pc, 15);
    const pcDark = darken(pc, 12);
    const pcHover = isDark ? lighten(pc, 8) : darken(pc, 8);
    const pcMuted = lighten(pc, 30);
    const textOnPc = hexToHSL(pc)[2] > 55 ? '#1F1B18' : '#FFFFFF';

    // Brand primary variables
    root.style.setProperty('--brand-primary', pc);
    root.style.setProperty('--brand-primary-light', pcLight);
    root.style.setProperty('--brand-primary-dark', pcDark);
    root.style.setProperty('--th-accent', pc);
    root.style.setProperty('--th-accent-h', pcHover);

    // Button overrides — use brand color for default buttons
    root.style.setProperty('--btn-bg', pc);
    root.style.setProperty('--btn-text', textOnPc);
    root.style.setProperty('--btn-hover', pcHover);
    root.style.setProperty('--btn-outline-border', pc);
    root.style.setProperty('--btn-outline-hover-bg', pc);
    root.style.setProperty('--btn-outline-hover-text', textOnPc);
    root.style.setProperty('--btn-ghost-hover-bg', withAlpha(pc, 0.1));

    // Sidebar active state
    root.style.setProperty('--sidebar-active', pc);
    root.style.setProperty('--sidebar-active-text', textOnPc);
  }

  if (ac) {
    root.style.setProperty('--brand-accent', ac);
    root.style.setProperty('--th-green', ac);
  }

  applyBrandFont(branding.font_family);
  applyBrandFontSize(branding.font_size);
};

const BRAND_VARS = [
  '--brand-primary','--brand-primary-light','--brand-primary-dark','--brand-accent',
  '--th-accent','--th-accent-h','--th-green',
  '--btn-bg','--btn-text','--btn-hover',
  '--btn-outline-border','--btn-outline-hover-bg','--btn-outline-hover-text','--btn-ghost-hover-bg',
  '--sidebar-active','--sidebar-active-text',
];

const clearBrandingFromDOM = () => {
  BRAND_VARS.forEach(v => document.documentElement.style.removeProperty(v));
  document.body.style.removeProperty("font-family");
  document.documentElement.style.removeProperty("font-size");
  document.documentElement.style.removeProperty("--brand-font");
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  // Tokens live in memory only — persistence is via httpOnly cookies set by the server
  const [token, setToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branding, setBranding] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [hrAccess, setHrAccess] = useState(null);   // per-account POS / drawer / clock-in switches from Staff HR
  const [enabledModules, setEnabledModules] = useState([]);
  const [setupCompleted, setSetupCompleted] = useState(true);
  const [trialInfo, setTrialInfo] = useState(null);

  // Silent session renewal: one in-flight refresh shared by every concurrent 401
  const refreshInFlight = useRef(null);
  const refreshTokenRef = useRef(null);
  refreshTokenRef.current = refreshToken;
  const refreshSession = useCallback(() => {
    if (refreshInFlight.current) return refreshInFlight.current;
    const body = refreshTokenRef.current ? { refresh_token: refreshTokenRef.current } : {};
    refreshInFlight.current = axios.post(`${API_URL}/auth/refresh`, body, { withCredentials: true, timeout: 15000 })
      .then((r) => {
        const d = r.data || {};
        if (!d.access_token) throw new Error("no token");
        setToken(d.access_token);
        if (d.refresh_token) setRefreshToken(d.refresh_token);
        return d.access_token;
      })
      .finally(() => { refreshInFlight.current = null; });
    return refreshInFlight.current;
  }, []);

  const api = useCallback(() => {
    const instance = axios.create({
      baseURL: API_URL,
      withCredentials: true,
      timeout: 30000,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    // Feed the connectivity manager: any HTTP response means the server is reachable;
    // only network-level failures (no response) trigger a health verification.
    instance.interceptors.response.use(
      (res) => { connectivity.noteApiSuccess(); return res; },
      async (err) => {
        if (err.response) connectivity.noteApiSuccess();
        else connectivity.noteApiFailure();
        // Retry ONLY transient failures, ONLY for GET (never risks duplicate sales/writes)
        const cfg = err.config || {};
        const status = err.response?.status;
        const url = cfg.url || "";
        const isAuthEndpoint = url.includes("/auth/login") || url.includes("/auth/refresh") || url.includes("/auth/logout");
        // Access token expired (8h) mid-shift: renew it silently from the refresh cookie and replay the request once.
        const hadSession = !!token || !!localStorage.getItem("auth_cache");
        if (status === 401 && hadSession && !isAuthEndpoint && !cfg._authRetry && (url.includes("/tenant/") || url.includes("/auth/me"))) {
          cfg._authRetry = true;
          try {
            const fresh = await refreshSession();
            const prev = cfg.headers && typeof cfg.headers.toJSON === "function" ? cfg.headers.toJSON() : (cfg.headers || {});
            cfg.headers = { ...prev, Authorization: `Bearer ${fresh}` };
            return axios(cfg);
          } catch {
            // Refresh failed → session is truly dead
          }
        }
        // Session expired mid-use: a 401 on any tenant API while logged in means the
        // session is dead — return to the login screen once instead of endless 401s.
        if (status === 401 && token && url.includes("/tenant/") && !window.__sessionExpiredHandled) {
          window.__sessionExpiredHandled = true;
          try {
            if (err.response?.data?.detail?.code === "session_expired") sessionStorage.setItem("logout_reason", "idle");
            localStorage.removeItem("token");
            localStorage.removeItem("refresh_token");
            localStorage.removeItem("auth_cache");
          } catch { /* */ }
          window.location.assign("/");
          return Promise.reject(err);
        }
        const transient = !err.response || [408, 429, 502, 503, 504].includes(status);
        if (transient && (cfg.method || "get").toLowerCase() === "get" && (cfg._retryCount || 0) < 3) {
          cfg._retryCount = (cfg._retryCount || 0) + 1;
          const delay = [1000, 2000, 4000][cfg._retryCount - 1] + Math.round(Math.random() * 250);
          await new Promise((r) => setTimeout(r, delay));
          return instance(cfg);
        }
        return Promise.reject(err);
      }
    );
    return instance;
  }, [token, refreshSession]);

  useEffect(() => {
    // The /platform (superadmin) area has its own session — no tenant staff probe there
    if (window.location.pathname.startsWith("/platform") && !token) {
      setLoading(false);
      return;
    }
    // Cookie session may exist even without an in-memory token — always check
    api().get("/auth/me").then((res) => {
      const d = res.data;
      setUser(d.user);
      setTenant(d.tenant);
      setPermissions(d.permissions || []);
      setHrAccess(d.hr_access || null);
      setEnabledModules(d.enabled_modules || []);
      setSetupCompleted(d.setup_completed !== false);
      if (d.tenant?.slug) {
        api().get("/tenant/settings/branding").then(r => {
          const b = r.data.branding || {};
          setBranding(b);
          applyBrandingToDOM(b);
        }).catch(() => {});
      }
      setLoading(false);
      try {
        localStorage.setItem("auth_cache", JSON.stringify({
          user: d.user, tenant: d.tenant, permissions: d.permissions || [],
          enabled_modules: d.enabled_modules || [], setup_completed: d.setup_completed !== false,
        }));
      } catch { /* */ }
    }).catch((err) => {
      const status = err.response?.status;
      if (status !== 401) {
        // Network failure / backend outage (5xx, timeout) — keep the last known identity so the POS can run from cache
        try {
          const cached = JSON.parse(localStorage.getItem("auth_cache") || "null");
          if (cached?.user) {
            setUser(cached.user);
            setTenant(cached.tenant || null);
            setPermissions(cached.permissions || []);
            setEnabledModules(cached.enabled_modules || []);
            setSetupCompleted(cached.setup_completed !== false);
          }
        } catch { /* */ }
        setLoading(false);
        return;
      }
      localStorage.removeItem("token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("auth_cache");
      setToken(null);
      setUser(null);
      setTenant(null);
      clearBrandingFromDOM();
      setLoading(false);
    });
  }, [token, api]);

  const login = async (username, pin, store) => {
    const body = { username, pin };
    if (store) body.store = store;
    const res = await axios.post(`${API_URL}/auth/login`, body, { withCredentials: true });
    const d = res.data;
    // Remember the store on this device (survives logout) so the login page opens on the right store
    try { if (d.tenant?.slug) { localStorage.setItem("pos_store_slug", d.tenant.slug); localStorage.setItem("pos_store_name", d.tenant.name || ""); } } catch { /* */ }
    // Legacy cleanup — session persistence is via httpOnly cookies now
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    setToken(d.access_token);
    setRefreshToken(d.refresh_token);
    setUser(d.user);
    setTenant(d.tenant);
    setPermissions(d.permissions || []);
    setHrAccess(d.hr_access || null);
    setEnabledModules(d.enabled_modules || []);
    setSetupCompleted(d.setup_completed !== false);
    setTrialInfo(d.trial);
    try {
      localStorage.setItem("auth_cache", JSON.stringify({
        user: d.user, tenant: d.tenant, permissions: d.permissions || [],
        enabled_modules: d.enabled_modules || [], setup_completed: d.setup_completed !== false,
      }));
    } catch { /* */ }
    // Clear tenant-scoped local storage on login to prevent cross-tenant leakage
    const tenantSlug = d.tenant?.slug;
    const prevSlug = localStorage.getItem("_active_tenant_slug");
    if (prevSlug && prevSlug !== tenantSlug) {
      // Different tenant — clear all tenant-scoped keys
      Object.keys(localStorage).forEach(k => {
        if (k.startsWith(`tenant_${prevSlug}_`)) localStorage.removeItem(k);
      });
    }
    if (tenantSlug) localStorage.setItem("_active_tenant_slug", tenantSlug);
    return d;
  };

  const logout = async () => {
    try { await api().post("/auth/logout"); } catch {}
    // Clear tenant-scoped data
    const slug = localStorage.getItem("_active_tenant_slug");
    if (slug) {
      Object.keys(localStorage).forEach(k => {
        if (k.startsWith(`tenant_${slug}_`)) localStorage.removeItem(k);
      });
    }
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("_active_tenant_slug");
    localStorage.removeItem("auth_cache");
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    setTenant(null);
    setBranding(null);
    setPermissions([]);
    setEnabledModules([]);
    clearBrandingFromDOM();
  };

  const apiInstance = useMemo(() => api(), [api]);

  // 30-minute inactivity sign-out (per device). Unfinished POS work is saved by the POS page on "pos:idle-logout".
  const touchSession = useCallback(() => { api().post("/auth/touch").catch(() => {}); }, [api]);
  const idleLogout = useCallback(async () => {
    window.dispatchEvent(new CustomEvent("pos:idle-logout"));
    await new Promise((r) => setTimeout(r, 400));
    try { sessionStorage.setItem("logout_reason", "idle"); } catch { /* */ }
    await logout();
    window.location.assign("/login");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const idle = useIdleGuard({ active: !!user && !!tenant, touch: touchSession, onIdleLogout: idleLogout });

  const hasPermission = (code) => permissions.includes(code);
  const hasModule = (code) => enabledModules.includes(code);
  const isTenantMode = !!tenant;

  const refreshBranding = useCallback(async () => {
    try {
      const r = await api().get("/tenant/settings/branding");
      const b = r.data.branding || {};
      setBranding(b);
      applyBrandingToDOM(b);
    } catch { /* */ }
  }, [api]);

  return (
    <AuthContext.Provider value={{
      user, tenant, token, branding, loading, hrAccess,
      permissions, enabledModules, setupCompleted, trialInfo, isTenantMode,
      login, logout, hasPermission, hasModule, refreshBranding, applyBrandingToDOM,
      api: apiInstance,
    }}>
      {children}
      <IdleWarningDialog secondsLeft={idle.secondsLeft} onStay={idle.stay} onLogout={idleLogout} />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
